import type { Utms } from '../utm';

declare global {
    interface Window {
        dataLayer: Array<unknown>;
        posthog?: {
            capture?: (event: string, properties?: Record<string, unknown>, options?: Record<string, unknown>) => void;
            identify?: (distinctId: string, properties?: Record<string, unknown>) => void;
            reset?: () => void;
        };
    }
}

export function trackPageView(url: string): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'page_view',
        page_location: url,
    });
}

export function trackSignup(method: 'email' | 'google', utms: Utms, howDidYouHear?: string): void {
    if (typeof window === 'undefined') return;
    const properties = { method, ...utms, ...(howDidYouHear ? { howDidYouHear } : {}) };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'signup_completed', ...properties });
    try {
        window.posthog?.capture?.('signup_completed', properties);
    } catch {
        /* noop */
    }
}

/**
 * A donor was genuinely SHOWN the ask — a surface naming real amounts, at
 * least half on screen. Never fired for a bare "Support my journey" button,
 * which names no figure and therefore answers no question about pricing.
 */
export function trackDonateAskViewed(properties: {
    amountsShown: true;
    amounts: number[];
    currency: string;
    surface: string;
    // TSF-308: every `donate_ask_viewed` in production is missing creatorId,
    // even though PostList passes a real uid and a raw HTTP capture proves
    // PostHog's own ingestion doesn't strip it. `?? null` at the call site
    // guarantees the key survives JSON.stringify either way -- if it was
    // silently `undefined` at capture time, future events will show
    // `creatorId:null` instead of no key at all, which tells the next
    // investigation whether the value was ever set, instead of looking
    // identical to "stripped downstream".
    creatorId?: string | null;
}): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'donate_ask_viewed', ...properties });
    try {
        window.posthog?.capture?.('donate_ask_viewed', properties);
    } catch {
        /* noop */
    }
}

export function trackPagePublished(): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'page_published' });
    try {
        window.posthog?.capture?.('page_published');
    } catch {
        /* noop */
    }
}

// Fired the moment a donor commits to a checkout call (after form validation
// passes, before the Cloud Function request goes out). Pairs with
// trackDonateAskViewed (impression) and the server-confirmed 'purchase'
// event to localize the donate_ask_viewed -> purchase drop-off: previously
// there was no client-side signal between seeing the ask and Stripe's own
// webhook confirming payment, so an abandoned checkout was indistinguishable
// from a visitor who never clicked Donate at all.
//
// TSF-291: this used to force { send_instantly: true, transport: 'sendBeacon' }
// on the theory that window.location.assign() to Stripe right after this call
// would race the default batched flush. That theory doesn't hold: both
// startOneOff/startMonthly (DonationForm.tsx) await the createOneOffCheckout/
// createMonthlyCheckout Cloud Function call -- a real network round trip --
// before they ever navigate, so there's no race to protect against. Meanwhile
// forcing navigator.sendBeacon() is the one thing privacy-hardened browsers
// (Brave Shields, several ad-block/anti-tracking extensions) specifically
// neuter while leaving ordinary fetch/XHR analytics calls alone -- confirmed
// live 2026-10-08: a direct capture() with this exact options object DOES
// produce a real sendBeacon network call in a clean browser (ingested
// successfully via raw curl against /ingest/e/ too), yet production logged
// zero checkout_started in 30 days despite 32 real donate_ask_viewed (plain
// capture(), no forced transport) and 6 real purchases. Matching
// trackDonateAskViewed's plain capture() removes the one dependency unique to
// this event.
export function trackCheckoutStarted(properties: {
    frequencyType: 'one_off' | 'monthly';
    amount: number;
    currency: string;
    creatorId: string;
}): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'checkout_started', ...properties });
    try {
        window.posthog?.capture?.('checkout_started', properties);
    } catch {
        /* noop */
    }
}

// TSF: the one stage of donate_ask_viewed -> checkout_started that had no
// signal at all -- a visitor can see the amounts card (donate_ask_viewed)
// without ever clicking Donate, so a low checkout_started count was
// indistinguishable from "nobody opens the dialog" vs "people open it and
// bail." This fires the moment the dialog actually opens, on the real public
// mission page only (not the creator's own /creator dashboard preview).
export function trackDonateDialogOpened(properties: { creatorId: string; surface?: string }): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'donate_dialog_opened', ...properties });
    try {
        window.posthog?.capture?.('donate_dialog_opened', properties);
    } catch {
        /* noop */
    }
}

export function trackCheckoutRedirectFailed(properties: {
    frequencyType: 'one_off' | 'monthly';
    creatorId: string;
    reason?: string;
}): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'checkout_redirect_failed', ...properties });
    try {
        window.posthog?.capture?.('checkout_redirect_failed', properties);
    } catch {
        /* noop */
    }
}

export function trackDonationCompleted(creatorId: string): void {
    if (typeof window === 'undefined') return;
    const properties = { creatorId };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'donation_completed', ...properties });
    try {
        window.posthog?.capture?.('donation_completed', properties);
    } catch {
        /* noop */
    }
}

export function trackStripeVerificationStarted(): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'stripe_verification_started' });
    try {
        window.posthog?.capture?.('stripe_verification_started');
    } catch {
        /* noop */
    }
}

// Fired the moment a creator asks to connect a bank account, before we know
// whether Stripe will actually issue an onboarding link. Pairs with
// trackStripeVerificationStarted (which only fires on success) so a failed
// attempt is distinguishable from never attempting at all -- previously the
// only way to tell those apart was a manual PostHog session trace.
export function trackStripeOnboardingAttempted(): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'stripe_onboarding_attempted' });
    try {
        window.posthog?.capture?.('stripe_onboarding_attempted');
    } catch {
        /* noop */
    }
}

export function trackStripeOnboardingFailed(reason?: string): void {
    if (typeof window === 'undefined') return;
    const properties = reason ? { reason } : undefined;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'stripe_onboarding_failed', ...(properties ?? {}) });
    try {
        window.posthog?.capture?.('stripe_onboarding_failed', properties);
    } catch {
        /* noop */
    }
}

export function trackStripeVerificationVerified(): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'stripe_verification_verified' });
    try {
        window.posthog?.capture?.('stripe_verification_verified');
    } catch {
        /* noop */
    }
}

export function trackDonationFeedback(rating: 'easy' | 'ok' | 'hard', creatorId: string): void {
    if (typeof window === 'undefined') return;
    const properties = { rating, creatorId };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'donation_feedback', ...properties });
    try {
        window.posthog?.capture?.('donation_feedback', properties);
    } catch {
        /* noop */
    }
}

export function trackPublishStalledFeedback(reason: 'bankStep' | 'notReady' | 'confused' | 'other'): void {
    if (typeof window === 'undefined') return;
    const properties = { reason };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'publish_stalled_feedback', ...properties });
    try {
        window.posthog?.capture?.('publish_stalled_feedback', properties);
    } catch {
        /* noop */
    }
}

export function trackKycFollowupRequested(reason: 'bankStep' | 'notReady' | 'confused' | 'other'): void {
    if (typeof window === 'undefined') return;
    const properties = { reason };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'kyc_followup_requested', ...properties });
    try {
        window.posthog?.capture?.('kyc_followup_requested', properties);
    } catch {
        /* noop */
    }
}

export function trackShareClicked(
    // 'fund_page' is the page a supporter is looking at. Until 2026-09-24 the
    // only share affordances were on a post card and on the creator's own
    // setup checklist, so a visitor who wanted to pass the athlete's page on
    // had to copy the address bar — while both sibling funds have carried a
    // Share button in the header the whole time.
    source: 'post_card' | 'checklist' | 'fund_page' | 'publish_toast',
    creatorId: string
): void {
    if (typeof window === 'undefined') return;
    const properties = { source, creatorId };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'share_clicked', ...properties });
    try {
        window.posthog?.capture?.('share_clicked', properties);
    } catch {
        /* noop */
    }
}

export function trackPageLoadTimedOut(page: string, creatorId?: string): void {
    if (typeof window === 'undefined') return;
    const properties = { page, creatorId };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'page_load_timed_out', ...properties });
    try {
        window.posthog?.capture?.('page_load_timed_out', properties);
    } catch {
        /* noop */
    }
}

export function identifySignedInUser(uid: string, properties?: Record<string, unknown>): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    // Only send an identified profile (which carries PII like email server-side
    // to PostHog) once the visitor has actively accepted the cookie banner's
    // persistent tier (see ConsentBanner.tsx / main.tsx). Without this check,
    // every signed-in user was identified regardless of consent state — "no
    // decision yet" and "declined" both bypassed the two-tier consent design
    // this analytics setup was built around.
    const consent = document.cookie.match(/(?:^|;\s*)sportfund_consent=([^;]+)/)?.[1] ?? null;
    if (consent !== 'granted') return;
    try {
        window.posthog?.identify?.(uid, properties);
    } catch {
        /* noop */
    }
}

export function resetIdentity(): void {
    if (typeof window === 'undefined') return;
    try {
        window.posthog?.reset?.();
    } catch {
        /* noop */
    }
}

export function trackVisitorIntent(intent: 'creator' | 'supporter' | 'researching' | 'dismissed'): void {
    if (typeof window === 'undefined') return;
    const properties = { intent };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'visitor_intent_selected', ...properties });
    try {
        window.posthog?.capture?.('visitor_intent_selected', properties);
    } catch {
        /* noop */
    }
}

export function trackArticleFeedback(articleSlug: string, rating: 'yes' | 'no'): void {
    if (typeof window === 'undefined') return;
    const properties = { articleSlug, rating };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'article_feedback', ...properties });
    try {
        window.posthog?.capture?.('article_feedback', properties);
    } catch {
        /* noop */
    }
}

export function trackSignupAbandonFeedback(reason: 'justBrowsing' | 'wantedInfoFirst' | 'tooComplicated' | 'other' | 'dismissed'): void {
    if (typeof window === 'undefined') return;
    const properties = { reason };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'signup_abandon_feedback', ...properties });
    try {
        window.posthog?.capture?.('signup_abandon_feedback', properties);
    } catch {
        /* noop */
    }
}

export function trackSigninAbandonFeedback(reason: 'noAccountYet' | 'forgotCredentials' | 'justLookingAround' | 'other' | 'dismissed'): void {
    if (typeof window === 'undefined') return;
    const properties = { reason };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'signin_abandon_feedback', ...properties });
    try {
        window.posthog?.capture?.('signin_abandon_feedback', properties);
    } catch {
        /* noop */
    }
}

// TSF-283: the shared cc.concept.zone chat widget reads its feedback question/
// options once from <script data-feedback-question/data-feedback-options>,
// site-wide -- it has no per-page API to ask a donation-specific question, and
// the widget itself lives in a different repo. This closes the gap with a
// donation-page-scoped prompt instead, reusing the same four reasons the
// widget already uses elsewhere on the site so the taxonomy stays one vocabulary.
export function trackDonationAbandonFeedback(reason: 'notReadyYet' | 'wantedMoreInfo' | 'askUnclear' | 'somethingBroken' | 'dismissed', creatorId: string): void {
    if (typeof window === 'undefined') return;
    const properties = { reason, creatorId };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'donation_abandon_feedback', ...properties });
    try {
        window.posthog?.capture?.('donation_abandon_feedback', properties);
    } catch {
        /* noop */
    }
}

// TSF-331: donate_ask_viewed -> checkout_started has a feedback prompt
// (trackDonationAbandonFeedback above); checkout_started -> purchase never
// did. This is the first qualitative signal from donors who reached Stripe's
// hosted checkout and came back via cancelUrl instead of completing payment.
export function trackCheckoutCancelFeedback(reason: 'priceTooHigh' | 'paymentMethodIssue' | 'changedMyMind' | 'somethingBroken' | 'dismissed', creatorId: string, frequencyType?: string): void {
    if (typeof window === 'undefined') return;
    const properties = { reason, creatorId, frequencyType };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'checkout_cancel_feedback', ...properties });
    try {
        window.posthog?.capture?.('checkout_cancel_feedback', properties);
    } catch {
        /* noop */
    }
}
